import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReturnToValidationBanner, { goBackToValidation } from '../common/ReturnToValidation';
import UploadDocumentosDialog from '../servicios/UploadDocumentosDialog';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Breadcrumbs,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  LinearProgress,
  Divider,
  Collapse,
  Snackbar,
  Autocomplete
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Store as StoreIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { FIELD_LIMITS } from '../../constants/fieldLimits';
import { validatePostalCode, validateGeolocation, ESTADOS_MEXICO } from '../utilerias/ValidationUtils';
import AddBusinessSectorModal from '../catalogos/AddBusinessSectorModal';
import { isJpgOrPng, JPG_PNG_ACCEPT } from '../../utils/imageFile';
import { capitalizePersonName } from '../../utils/textCase';
import CaseTypoHint from '../common/CaseTypoHint';

// Mismas opciones que CatalogoEmpresas.jsx para el Título del contacto de la matriz --
// mantenerlas consistentes entre ambos formularios.
const COMMON_CONTACT_TITLES = ['Lic.', 'Ing.', 'Arq.', 'Dr.', 'Dra.', 'Mtro.', 'Mtra.', 'C.P.', 'Sr.', 'Sra.', 'Srta.'];

const GestionSucursales = () => {
  const location = useLocation();
  // Si llegó desde "Validar Campos" (Programas): al guardar regresa solo a la validación
  const [returnToValidation] = useState(() => location.state?.returnToValidation || null);
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  // loadError: falló la carga inicial (se muestra en toda la pantalla). error: falló una acción
  // (guardar, eliminar, validar...) -- se muestra en el diálogo o arriba de la lista sin tumbar la
  // pantalla; antes ambos usaban `error` y cualquier fallo al guardar reemplazaba toda la vista.
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [generatedDocFolderPath, setGeneratedDocFolderPath] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byCompany: {}
  });

  // Get URL parameters for company context
  const urlParams = new URLSearchParams(location.search);
  const selectedCompanyId = urlParams.get('companyId');
  const selectedCompanyName = urlParams.get('companyName');
  
  const [filters, setFilters] = useState({
    branchName: '',
    companyId: selectedCompanyId || '',
    city: '',
    isActive: ''
  });

  // Estado para errores de validación de código postal
  const [postalCodeValidation, setPostalCodeValidation] = useState({ isValid: true, message: '' });
  const [emailValidation, setEmailValidation] = useState({ isValid: true, message: '' });
  const [phoneValidation, setPhoneValidation] = useState({ isValid: true, message: '' });
  const [branchNameValidation, setBranchNameValidation] = useState({ isValid: true, message: '' });
  const [geolocationValidation, setGeolocationValidation] = useState({ isValid: true, message: '' });
  const [createAddressErrors, setCreateAddressErrors] = useState({});
  const [createFormError, setCreateFormError] = useState('');
  const createDialogContentRef = useRef(null);

  const requiredCreateAddressFields = {
    street: 'Calle',
    exteriorNumber: 'Número Exterior',
    neighborhood: 'Colonia/Barrio',
    municipality: 'Municipio/Delegación',
    city: 'Localidad',
    state: 'Estado',
    postalCode: 'Código Postal',
    country: 'País',
    geolocation: 'Georeferencia'
  };
  
  // Estado para sectores empresariales
  const [businessSectors, setBusinessSectors] = useState([]);
  const [sectorModalOpen, setSectorModalOpen] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [pendingHighlightFieldKey, setPendingHighlightFieldKey] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [deletingBranch, setDeletingBranch] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [editBranch, setEditBranch] = useState({
    branchName: '',
    businessSector: '',
    address: '',
    street: '',
    exteriorNumber: '',
    interiorNumber: '',
    neighborhood: '',
    municipality: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'México',
    reference: '',
    betweenStreets: '',
    geolocation: '',
    contactTitle: '',
    contactFirstName: '',
    contactPaternalSurname: '',
    contactMaternalSurname: '',
    contactEmail: '',
    contactPhone: '',
    insuranceCompany: '',
    insurancePolicyNumber: '',
    insurancePolicyValidity: '',
    employeesCount: '',
    maximumCapacity: '',
    floatingPopulation: '',
    mainActivities: '',
    operatingHoursStart: '',
    operatingHoursEnd: '',
    administrativeHours: '',
    administrativeHoursStart: '',
    administrativeHoursEnd: '',
    constructionLevels: '',
    constructionArea: '',
    landArea: '',
    companyId: selectedCompanyId || '',
    propertyRegime: 'PROPIO',
    isActive: true
  });

  // Estados para gestión de documentos
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedBranchForDocs, setSelectedBranchForDocs] = useState(null);

  // Estados para validación y generación de documentos
  const [docValidationDialogOpen, setDocValidationDialogOpen] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [generatingDoc, setGeneratingDoc] = useState(null); // 'ric' | 'pipc' | null
  const [selectedBranchForGen, setSelectedBranchForGen] = useState(null);

  // Estado para controlar qué acordeones están expandidos
  const [expandedCompanies, setExpandedCompanies] = useState({});

  // Estados para foto del inmueble de la sucursal
  const [branchPropertyImageFile, setBranchPropertyImageFile] = useState(null);
  const [branchPropertyImagePreview, setBranchPropertyImagePreview] = useState(null);
  const [branchPropertyImageUploading, setBranchPropertyImageUploading] = useState(false);
  const [branchPropertyImageSuccess, setBranchPropertyImageSuccess] = useState(false);
  const [branchPropertyImageError, setBranchPropertyImageError] = useState('');
  const [branchPropertyImagePreviewLoading, setBranchPropertyImagePreviewLoading] = useState(false);
  const branchPropertyImageInputRef = useRef(null);

  // Trae la imagen YA guardada en Dropbox y la muestra como preview real, en vez de solo el
  // check "hay un archivo guardado" -- mismo criterio que CatalogoEmpresas.loadSavedImagePreview.
  // Si el backend dice que hay archivo (hasPropertyImage) pero la descarga falla, es evidencia de
  // que en realidad no se guardó bien (o el archivo ya no existe en Dropbox).
  const loadSavedBranchImagePreview = async (branchId) => {
    setBranchPropertyImagePreviewLoading(true);
    try {
      const response = await api.get(`/company-branches/${branchId}/property-image`, { responseType: 'blob' });
      setBranchPropertyImagePreview(URL.createObjectURL(response.data));
    } catch (err) {
      setBranchPropertyImagePreview(null);
      setBranchPropertyImageError('El sistema indica que hay una fotografía guardada, pero no se pudo descargar desde Dropbox. Probablemente la subida no se completó correctamente.');
    } finally {
      setBranchPropertyImagePreviewLoading(false);
    }
  };

  const handleBranchPropertyImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isJpgOrPng(file)) {
      setBranchPropertyImageError('Solo se permiten archivos .jpg o .png');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setBranchPropertyImageError('El archivo no puede superar 8 MB');
      return;
    }
    setBranchPropertyImageFile(file);
    setBranchPropertyImagePreview(URL.createObjectURL(file));
    setBranchPropertyImageError('');
  };

  const handleBranchPropertyImageUpload = async () => {
    if (!branchPropertyImageFile || !selectedBranch?.id) return;
    setBranchPropertyImageUploading(true);
    setBranchPropertyImageError('');
    try {
      const formData = new FormData();
      formData.append('file', branchPropertyImageFile);
      await api.post(`/company-branches/${selectedBranch.id}/property-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBranchPropertyImageSuccess(true);
      setBranchPropertyImageFile(null);
      setSelectedBranch(prev => prev ? { ...prev, hasPropertyImage: true } : prev);
      setTimeout(() => setBranchPropertyImageSuccess(false), 4000);
    } catch (err) {
      setBranchPropertyImageError(err.response?.data?.error || 'Error al subir la fotografía. Intenta de nuevo.');
    } finally {
      setBranchPropertyImageUploading(false);
    }
  };

  // Bloqueo de datos del inmueble/contacto mientras la sucursal tenga una cotización enviada u OT abierta
  const [entityLock, setEntityLock] = useState(null);
  // Administradores sí pueden corregir; para los demás los campos bloqueados quedan deshabilitados
  const lockFields = !!(entityLock?.locked && !entityLock?.canOverride && !entityLock?.editableWithNotice);

  const isEmpty = (val) =>val === null || val === undefined || String(val).trim() === '';

  const warnSx = (warn) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      ...(warn && { '& fieldset': { borderColor: '#ed6c02' } }),
      '&:hover fieldset': { borderColor: warn ? '#ed6c02' : '#1976d2' },
    },
    '& .MuiInputLabel-root': { color: warn ? '#ed6c02' : '#000000' },
  });

  const warnSelectSx = (warn) => ({
    borderRadius: 2,
    ...(warn && { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ed6c02' } }),
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: warn ? '#ed6c02' : '#1976d2' },
  });

  // Mismos rangos que CatalogoEmpresas.jsx/Programas.jsx/NewQuotation.jsx -- es el mismo dato
  // (company_branches.employees_count/construction_area/land_area), así que debe validar igual
  // sin importar desde qué pantalla se edite.
  const PROPERTY_FIELD_LIMITS = {
    employeesCount:   { ...FIELD_LIMITS.employeesCount, label: 'empleados' },
    constructionArea: { ...FIELD_LIMITS.constructionArea, label: 'm² de construcción' },
    landArea:         { ...FIELD_LIMITS.landArea, label: 'm² de terreno' },
  };
  const propertyFieldHelperText = (field) => {
    const limits = PROPERTY_FIELD_LIMITS[field];
    return `Entre ${limits.min} y ${limits.max.toLocaleString('es-MX')} ${limits.label}`;
  };
  const handlePropertyFieldChange = (field, rawValue) => {
    const limits = PROPERTY_FIELD_LIMITS[field];
    const allowDecimal = field === 'constructionArea' || field === 'landArea';
    let sanitized = rawValue.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');
    if (allowDecimal) {
      const firstDot = sanitized.indexOf('.');
      if (firstDot !== -1) {
        sanitized = sanitized.slice(0, firstDot + 1) + sanitized.slice(firstDot + 1).replace(/\./g, '');
      }
    }
    if (sanitized !== '' && sanitized !== '.' && Number(sanitized) > limits.max) return;
    setEditBranch(prev => ({ ...prev, [field]: sanitized }));
  };

  // Campos obligatorios: misma lógica que Prospectos (CatalogoEmpresas.jsx) -- etiqueta y
  // caja en un tono dorado tenue de forma permanente, y el naranja de "vacío" (warn) gana
  // solo cuando corresponde (al editar una sucursal existente que le falta un campo).
  const REQUIRED_LABEL_COLOR = '#9c7c38';
  const REQUIRED_FIELD_BG = '#fbf6ea';
  const REQUIRED_FIELD_BORDER = '#dcc48a';
  const requiredLabel = (text) => (
    <Box component="span" sx={{ color: REQUIRED_LABEL_COLOR }}>{text} *</Box>
  );
  const requiredSx = (warn) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: warn ? undefined : REQUIRED_FIELD_BG,
      '& fieldset': { borderColor: warn ? '#ed6c02' : REQUIRED_FIELD_BORDER },
      '&:hover fieldset': { borderColor: warn ? '#ed6c02' : REQUIRED_LABEL_COLOR },
    },
    '& .MuiInputLabel-root': { color: warn ? '#ed6c02' : '#000000' },
  });
  const requiredSelectSx = (warn) => ({
    borderRadius: 2,
    backgroundColor: warn ? undefined : REQUIRED_FIELD_BG,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: warn ? '#ed6c02' : REQUIRED_FIELD_BORDER },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: warn ? '#ed6c02' : REQUIRED_LABEL_COLOR },
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Si llegamos desde otra pantalla (ej. Programas, al revisar campos faltantes) con una
  // sucursal específica a resaltar, abrir directamente su diálogo de edición en cuanto
  // los datos estén cargados, en vez de dejar al usuario buscarla en la lista.
  useEffect(() => {
    if (!location.state?.highlightEntityId) return;
    if (location.state.entityType && location.state.entityType !== 'SUCURSAL') return;
    if (branches.length === 0) return;

    const branch = branches.find(b => String(b.id) === String(location.state.highlightEntityId));
    if (branch) {
      handleEditClick(branch);
      if (location.state.highlightFieldKey) {
        setPendingHighlightFieldKey(location.state.highlightFieldKey);
      }
    }
    // Limpiar el state para no reabrir el diálogo en re-renders o al volver con "atrás"
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [branches, location.state]);

  // Una vez abierto el diálogo de edición (arriba), si venimos con un campo específico a
  // resaltar (click en "Validación de Campos" desde Programas.jsx), lo desplazamos a la vista
  // y lo resaltamos brevemente para que el usuario lo ubique sin buscar en todo el formulario.
  useEffect(() => {
    if (!editDialogOpen || !pendingHighlightFieldKey) return;
    const key = pendingHighlightFieldKey;
    const timer = setTimeout(() => {
      const el = document.getElementById(`field-${key}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const target = el.closest('.MuiFormControl-root') || el.closest('.MuiPaper-root') || el;
        target.style.transition = 'box-shadow 0.3s ease';
        target.style.boxShadow = '0 0 0 3px #ff9800';
        target.style.borderRadius = target.style.borderRadius || '8px';
        setTimeout(() => { target.style.boxShadow = ''; }, 2500);
      }
      setPendingHighlightFieldKey(null);
    }, 400);
    return () => clearTimeout(timer);
  }, [editDialogOpen, pendingHighlightFieldKey]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [branchesResponse, companiesResponse, sectorsResponse] = await Promise.all([
        api.get('/company-branches'),
        api.get('/companies'),
        api.get('/business-sectors')
      ]);
      
      const branchesData = branchesResponse.data || [];
      const companiesData = companiesResponse.data || [];
      const sectorsData = sectorsResponse.data || [];
      
      setBranches(branchesData);
      setCompanies(companiesData);
      setBusinessSectors(sectorsData);
      setFilteredBranches(branchesData);
      calculateStats(branchesData, companiesData);
      console.log('Sectores empresariales cargados:', sectorsData.length);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error al cargar los datos';
      setLoadError(`Error al cargar los datos: ${errorMessage}`);
      console.error('Error completo:', err);
      console.error('Error response:', err.response);
      
      // Inicializar con arrays vacíos en caso de error
      setBranches([]);
      setCompanies([]);
      setFilteredBranches([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (branchesData, companiesData) => {
    const byCompany = {};
    companiesData.forEach(company => {
      byCompany[company.id] = {
        name: company.businessName,
        count: branchesData.filter(b => b.companyId === company.id).length
      };
    });

    const stats = {
      total: branchesData.length,
      active: branchesData.filter(b => b.isActive).length,
      inactive: branchesData.filter(b => !b.isActive).length,
      byCompany
    };
    setStats(stats);
  };

  const handleSectorAdded = async (newSector) => {
    try {
      const response = await api.get('/business-sectors');
      setBusinessSectors(response.data);
      setEditBranch(prev => ({ ...prev, businessSector: newSector.id }));
      console.log(`Sector "${newSector.name}" agregado y seleccionado`);
    } catch (err) {
      console.error('Error al recargar sectores:', err);
    }
  };

  // Filtrar sucursales
  useEffect(() => {
    let filtered = branches;

    if (filters.branchName) {
      filtered = filtered.filter(b => 
        b.branchName?.toLowerCase().includes(filters.branchName.toLowerCase())
      );
    }

    if (filters.companyId) {
      filtered = filtered.filter(b => b.companyId === parseInt(filters.companyId));
    }

    if (filters.city) {
      filtered = filtered.filter(b => 
        b.city?.toLowerCase().includes(filters.city.toLowerCase())
      );
    }

    if (filters.isActive !== '') {
      filtered = filtered.filter(b => b.isActive === (filters.isActive === 'true'));
    }

    setFilteredBranches(filtered);
  }, [filters.branchName, filters.companyId, filters.city, filters.isActive, branches]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const hasCreateFormData = () => {
    const fieldsToCheck = [
      'branchName', 'street', 'exteriorNumber', 'neighborhood',
      'municipality', 'city', 'postalCode', 'geolocation',
      'contactTitle', 'contactFirstName', 'contactPaternalSurname', 'contactMaternalSurname',
      'contactEmail', 'contactPhone',
    ];
    return fieldsToCheck.some(f => editBranch[f] && String(editBranch[f]).trim() !== '');
  };

  const handleCreateCloseAttempt = () => {
    if (hasCreateFormData()) {
      setDiscardConfirmOpen(true);
    } else {
      setCreateDialogOpen(false);
      setCreateAddressErrors({});
      resetForm();
    }
  };

  const resetForm = () => {
    setEditBranch({
      branchName: '',
      businessSector: '',
      address: '',
      street: '',
      exteriorNumber: '',
      interiorNumber: '',
      neighborhood: '',
      municipality: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'México',
      reference: '',
      betweenStreets: '',
      geolocation: '',
      contactTitle: '',
      contactFirstName: '',
      contactPaternalSurname: '',
      contactMaternalSurname: '',
      contactEmail: '',
      contactPhone: '',
      insuranceCompany: '',
      insurancePolicyNumber: '',
      insurancePolicyValidity: '',
      employeesCount: '',
      maximumCapacity: '',
      floatingPopulation: '',
      mainActivities: '',
      operatingHoursStart: '',
      operatingHoursEnd: '',
      administrativeHours: '',
      administrativeHoursStart: '',
      administrativeHoursEnd: '',
      constructionLevels: '',
      constructionArea: '',
      landArea: '',
      companyId: selectedCompanyId || '',
      isActive: true
    });
    setPostalCodeValidation({ isValid: true, message: '' });
    setEmailValidation({ isValid: true, message: '' });
    setPhoneValidation({ isValid: true, message: '' });
  };

  // Función para validar formato de email
  const validateEmailFormat = (email) => {
    if (!email || email.trim() === '') {
      return { isValid: true, message: '' }; // Email es opcional
    }
    
    // Regex para validar formato de email
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email.trim())) {
      return { 
        isValid: false, 
        message: 'Formato de email inválido. Debe incluir @dominio.com' 
      };
    }
    
    return { isValid: true, message: '�o" Formato de email válido' };
  };

  // Función para validar formato de teléfono
  const validatePhoneFormat = (phone) => {
    if (!phone || phone.trim() === '') {
      return { isValid: true, message: '' }; // Teléfono es opcional
    }
    
    // Eliminar caracteres no numéricos para validar
    const numericPhone = phone.replace(/[^0-9]/g, '');
    
    if (numericPhone.length !== 10) {
      return { 
        isValid: false, 
        message: 'El teléfono debe tener exactamente 10 dígitos' 
      };
    }
    
    return { isValid: true, message: '�o" Formato de teléfono válido' };
  };

  // Manejador para cambio de email con validación
  const handleEmailChange = (newEmail) => {
    setEditBranch(prev => ({ ...prev, contactEmail: newEmail }));
    const validation = validateEmailFormat(newEmail);
    setEmailValidation(validation);
  };

  // Manejador para cambio de teléfono con validación y limitación a 10 dígitos
  const handlePhoneChange = (newPhone) => {
    // Limitar a solo números y máximo 10 dígitos
    const numericValue = newPhone.replace(/[^0-9]/g, '');
    if (numericValue.length <= 10) {
      setEditBranch(prev => ({ ...prev, contactPhone: numericValue }));
      const validation = validatePhoneFormat(numericValue);
      setPhoneValidation(validation);
    }
  };

  const handleOpenCreateDialog = () => {
    // Si la pantalla viene en contexto de una empresa (companyId en URL),
    // fijar esa empresa pero NO precargar la dirección desde la matriz.
    const companyId = selectedCompanyId ? parseInt(selectedCompanyId) : null;
    const selectedCompany = companyId ? companies.find(c => c.id === companyId) : null;
    
    // Obtener el primer sector empresarial disponible
    const defaultSector = businessSectors.length > 0 ? businessSectors[0].id : '';

    if (selectedCompany) {
      const prefilledContactTitle = selectedCompany.corporateContactTitle || '';
      const prefilledContactFirstName = selectedCompany.corporateContactFirstName || '';
      const prefilledContactPaternalSurname = selectedCompany.corporateContactPaternalSurname || '';
      const prefilledContactMaternalSurname = selectedCompany.corporateContactMaternalSurname || '';
      const prefilledContactEmail = selectedCompany.corporateContactEmail || '';
      const prefilledContactPhone = selectedCompany.corporateContactPhone || '';
      // Si la empresa marcó "misma póliza para todas las sucursales", precargamos su seguro
      const sameInsurance = !!selectedCompany.insuranceSameForBranches;

      setEditBranch(prev => ({
        ...prev,
        companyId: companyId,
        businessSector: defaultSector,
        // Todos los campos de dirección se inicializan vacíos
        street: '',
        exteriorNumber: '',
        interiorNumber: '',
        neighborhood: '',
        municipality: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'México',
        reference: '',
        betweenStreets: '',
        geolocation: '',
        contactTitle: (prev.contactTitle || '').trim() ? prev.contactTitle : prefilledContactTitle,
        contactFirstName: (prev.contactFirstName || '').trim() ? prev.contactFirstName : prefilledContactFirstName,
        contactPaternalSurname: (prev.contactPaternalSurname || '').trim() ? prev.contactPaternalSurname : prefilledContactPaternalSurname,
        contactMaternalSurname: (prev.contactMaternalSurname || '').trim() ? prev.contactMaternalSurname : prefilledContactMaternalSurname,
        contactEmail: (prev.contactEmail || '').trim() ? prev.contactEmail : prefilledContactEmail,
        contactPhone: (prev.contactPhone || '').trim() ? prev.contactPhone : prefilledContactPhone,
        insuranceCompany: sameInsurance ? (selectedCompany.insuranceCompany || '') : prev.insuranceCompany,
        insurancePolicyNumber: sameInsurance ? (selectedCompany.insurancePolicyNumber || '') : prev.insurancePolicyNumber,
        insurancePolicyValidity: sameInsurance ? (selectedCompany.insurancePolicyValidity || '') : prev.insurancePolicyValidity,
      }));

      setEmailValidation(validateEmailFormat((editBranch.contactEmail || '').trim() ? editBranch.contactEmail : prefilledContactEmail));
      setPhoneValidation(validatePhoneFormat((editBranch.contactPhone || '').trim() ? editBranch.contactPhone : prefilledContactPhone));
    } else {
      // Si no hay empresa seleccionada, establecer la primera empresa de la lista y primer giro
      const firstCompany = companies.length > 0 ? companies[0] : null;
      
      if (firstCompany) {
        const prefilledContactTitle = firstCompany.corporateContactTitle || '';
        const prefilledContactFirstName = firstCompany.corporateContactFirstName || '';
        const prefilledContactPaternalSurname = firstCompany.corporateContactPaternalSurname || '';
        const prefilledContactMaternalSurname = firstCompany.corporateContactMaternalSurname || '';
        const prefilledContactEmail = firstCompany.corporateContactEmail || '';
        const prefilledContactPhone = firstCompany.corporateContactPhone || '';
        const sameInsurance = !!firstCompany.insuranceSameForBranches;

        setEditBranch(prev => ({
          ...prev,
          companyId: firstCompany.id,
          businessSector: defaultSector,
          // Todos los campos de dirección se inicializan vacíos
          street: '',
          exteriorNumber: '',
          interiorNumber: '',
          neighborhood: '',
          municipality: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'México',
          reference: '',
          betweenStreets: '',
          geolocation: '',
          contactTitle: (prev.contactTitle || '').trim() ? prev.contactTitle : prefilledContactTitle,
          contactFirstName: (prev.contactFirstName || '').trim() ? prev.contactFirstName : prefilledContactFirstName,
          contactPaternalSurname: (prev.contactPaternalSurname || '').trim() ? prev.contactPaternalSurname : prefilledContactPaternalSurname,
          contactMaternalSurname: (prev.contactMaternalSurname || '').trim() ? prev.contactMaternalSurname : prefilledContactMaternalSurname,
          contactEmail: (prev.contactEmail || '').trim() ? prev.contactEmail : prefilledContactEmail,
          contactPhone: (prev.contactPhone || '').trim() ? prev.contactPhone : prefilledContactPhone,
          insuranceCompany: sameInsurance ? (firstCompany.insuranceCompany || '') : prev.insuranceCompany,
          insurancePolicyNumber: sameInsurance ? (firstCompany.insurancePolicyNumber || '') : prev.insurancePolicyNumber,
          insurancePolicyValidity: sameInsurance ? (firstCompany.insurancePolicyValidity || '') : prev.insurancePolicyValidity,
        }));

        setEmailValidation(validateEmailFormat((editBranch.contactEmail || '').trim() ? editBranch.contactEmail : prefilledContactEmail));
        setPhoneValidation(validatePhoneFormat((editBranch.contactPhone || '').trim() ? editBranch.contactPhone : prefilledContactPhone));
      } else {
        // Si no hay empresas, solo establecer el giro por defecto
        setEditBranch(prev => ({
          ...prev,
          businessSector: defaultSector
        }));

        setEmailValidation({ isValid: true, message: '' });
        setPhoneValidation({ isValid: true, message: '' });
      }
    }

    setBranchNameValidation({ isValid: true, message: '' });
    setCreateAddressErrors({});
    setCreateFormError('');
    setCreateDialogOpen(true);
  };

  const getCompanyName = (companyId) => {
    // Convertir a número para comparación correcta
    const id = parseInt(companyId);
    const company = companies.find(c => c.id === id);
    return company ? company.businessName : `Empresa ID: ${companyId}`;
  };

  const handleEditClick = (branch) => {
    setError(null);
    setEntityLock(null);
    api.get(`/company-branches/${branch.id}/lock-status`)
      .then(res => setEntityLock(res.data))
      .catch(() => setEntityLock(null));
    setSelectedBranch(branch);
    setBranchPropertyImageFile(null);
    setBranchPropertyImagePreview(null);
    setBranchPropertyImageError('');
    if (branch.id && branch.hasPropertyImage) loadSavedBranchImagePreview(branch.id);

    // El backend ahora devuelve businessSectorId (Integer)
    // Usar businessSectorId si está disponible, sino usar businessSector
    const businessSectorValue = branch.businessSectorId || branch.businessSector || '';
    
    console.log('�Y"� DEBUG - Cargando sucursal para edición:');
    console.log('   Branch completo:', branch);
    console.log('   businessSectorId:', branch.businessSectorId, 'tipo:', typeof branch.businessSectorId);
    console.log('   businessSector:', branch.businessSector, 'tipo:', typeof branch.businessSector);
    console.log('   businessSector final:', businessSectorValue, 'tipo:', typeof businessSectorValue);
    console.log('   Sectores disponibles:', businessSectors.map(s => ({ id: s.id, name: s.name })));
    
    setEditBranch({
      branchName: branch.branchName || '',
      businessSector: businessSectorValue,
      address: branch.address || '',
      street: branch.street || '',
      exteriorNumber: branch.exteriorNumber || '',
      interiorNumber: branch.interiorNumber || '',
      neighborhood: branch.neighborhood || '',
      municipality: branch.municipality || '',
      city: branch.city || '',
      state: branch.state || '',
      postalCode: branch.postalCode || '',
      country: branch.country || 'México',
      reference: branch.reference || '',
      betweenStreets: branch.betweenStreets || '',
      geolocation: branch.geolocation || '',
      contactTitle: branch.contactTitle || '',
      contactFirstName: branch.contactFirstName || '',
      contactPaternalSurname: branch.contactPaternalSurname || '',
      contactMaternalSurname: branch.contactMaternalSurname || '',
      contactEmail: branch.contactEmail || '',
      contactPhone: branch.contactPhone || '',
      insuranceCompany: branch.insuranceCompany || '',
      insurancePolicyNumber: branch.insurancePolicyNumber || '',
      insurancePolicyValidity: branch.insurancePolicyValidity || '',
      employeesCount: branch.employeesCount != null ? branch.employeesCount : '',
      maximumCapacity: branch.maximumCapacity != null ? branch.maximumCapacity : '',
      floatingPopulation: branch.floatingPopulation != null ? branch.floatingPopulation : '',
      mainActivities: branch.mainActivities || '',
      operatingHoursStart: branch.operatingHoursStart || '',
      operatingHoursEnd: branch.operatingHoursEnd || '',
      administrativeHours: branch.administrativeHours || '',
      administrativeHoursStart: branch.administrativeHoursStart || '',
      administrativeHoursEnd: branch.administrativeHoursEnd || '',
      constructionLevels: branch.constructionLevels != null ? branch.constructionLevels : '',
      constructionArea: branch.constructionArea != null ? branch.constructionArea : '',
      landArea: branch.landArea != null ? branch.landArea : '',
      companyId: branch.companyId || '',
      propertyRegime: branch.propertyRegime || 'PROPIO',
      isActive: branch.isActive !== undefined ? branch.isActive : true
    });
    const emailValidation = validateEmailFormat(branch.contactEmail);
    setEmailValidation(emailValidation);
    const phoneValidation = validatePhoneFormat(branch.contactPhone);
    setPhoneValidation(phoneValidation);
    setEditDialogOpen(true);
  };

  const handleAlphanumericChange = (field, value) => {
    const cleaned = value.replace(/[^a-zA-Z0-9/-]/g, '');
    setEditBranch(prev => ({ ...prev, [field]: cleaned }));
    setCreateAddressErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleTextChange = (field, value) => {
    const cleaned = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚüÜñÑ0-9\s\-.,']/g, '');
    setEditBranch(prev => ({ ...prev, [field]: cleaned }));
    setCreateAddressErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleGeolocationChange = (value) => {
    setEditBranch(prev => ({ ...prev, geolocation: value }));
    setCreateAddressErrors(prev => ({ ...prev, geolocation: '' }));
    const validation = validateGeolocation(value);
    setGeolocationValidation(validation);
  };

  const handleNumericChange = (field, value) => {
    const numericValue = value.replace(/[^0-9]/g, '').slice(0, 5);
    setEditBranch(prev => ({
      ...prev,
      [field]: numericValue
    }));
    setCreateAddressErrors(prev => ({ ...prev, [field]: '' }));

    if (field === 'postalCode') {
      const validation = validatePostalCode(numericValue);
      setPostalCodeValidation(validation);
    }
  };

  const handleEditSubmit = async () => {
    try {
      if (!selectedBranch) {
        setError('No se ha seleccionado ninguna sucursal para editar');
        return;
      }

      if (!editBranch.branchName || !editBranch.companyId) {
        setError('El nombre de la sucursal y la empresa son requeridos');
        return;
      }

      if (!editBranch.businessSector) {
        setError('El giro de la sucursal es requerido');
        return;
      }

      // Validar código postal
      const postalValidation = validatePostalCode(editBranch.postalCode);
      setPostalCodeValidation(postalValidation);
      if (!postalValidation.isValid) {
        return;
      }

      // Validar georeferencia
      const geoValidation = validateGeolocation(editBranch.geolocation);
      setGeolocationValidation(geoValidation);
      if (!geoValidation.isValid) {
        return;
      }

      console.log('�Y"� Enviando datos de sucursal al backend:', editBranch);
      console.log('   businessSector a enviar:', editBranch.businessSector, 'tipo:', typeof editBranch.businessSector);

      const dataToSend = { ...editBranch };

      const response = await api.put(`/company-branches/${selectedBranch.id}`, dataToSend);
      const updatedBranches = branches.map(b => b.id === selectedBranch.id ? response.data : b);
      setBranches(updatedBranches);
      setFilteredBranches(updatedBranches);
      calculateStats(updatedBranches, companies);
      setEditDialogOpen(false);
      setError(null);
      setSelectedBranch(null);
      resetForm();
      if (returnToValidation) {
        goBackToValidation(navigate, returnToValidation);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al editar la sucursal');
      console.error('Error:', err);
    }
  };

  const handleCreateSubmit = async () => {
    // --- Validación (fuera del try-catch para no enmascarar errores) ---
    let hasErrors = false;
    const missingLabels = [];

    const branchNameOk = editBranch.branchName && editBranch.branchName.trim() !== '';
    setBranchNameValidation(branchNameOk
      ? { isValid: true, message: '' }
      : { isValid: false, message: 'Este campo es obligatorio' }
    );
    if (!branchNameOk) {
      hasErrors = true;
      missingLabels.push('Nombre de la Sucursal');
    }

    const newAddressErrors = {};
    Object.entries(requiredCreateAddressFields).forEach(([field, label]) => {
      if (!editBranch[field] || String(editBranch[field]).trim() === '') {
        newAddressErrors[field] = `${label} es obligatorio`;
        missingLabels.push(label);
      }
    });
    setCreateAddressErrors(newAddressErrors);
    if (Object.keys(newAddressErrors).length > 0) hasErrors = true;

    const postalValidation = editBranch.postalCode
      ? validatePostalCode(editBranch.postalCode)
      : { isValid: true, message: '' };
    setPostalCodeValidation(postalValidation);
    if (!postalValidation.isValid) hasErrors = true;

    const geoValidation = editBranch.geolocation
      ? validateGeolocation(editBranch.geolocation)
      : { isValid: true, message: '' };
    setGeolocationValidation(geoValidation);
    if (!geoValidation.isValid) hasErrors = true;

    if (!editBranch.companyId) {
      hasErrors = true;
      missingLabels.push('Empresa');
    }
    if (!editBranch.businessSector) {
      hasErrors = true;
      missingLabels.push('Giro de la Sucursal');
    }

    if (hasErrors) {
      const summary = missingLabels.length > 0
        ? `Faltan campos obligatorios: ${missingLabels.join(', ')}.`
        : 'Revisa los campos marcados en rojo antes de continuar.';
      setCreateFormError(summary);
      if (createDialogContentRef.current) {
        createDialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    setCreateFormError('');

    // --- API call ---
    try {
      const dataToSend = { ...editBranch };

      const response = await api.post('/company-branches', dataToSend);
      const newBranches = [...branches, response.data];
      setBranches(newBranches);
      setFilteredBranches(newBranches);
      calculateStats(newBranches, companies);
      setCreateDialogOpen(false);
      setCreateFormError('');
      resetForm();
    } catch (err) {
      setCreateFormError('Error al crear la sucursal. Intenta de nuevo.');
      if (createDialogContentRef.current) {
        createDialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      console.error('Error al crear sucursal:', err);
    }
  };

  const handleDeleteClick = (branch) => {
    setBranchToDelete(branch);
  };

  const handleConfirmDeleteBranch = async () => {
    const branch = branchToDelete;
    if (!branch) return;
    try {
      setDeletingBranch(true);
      await api.delete(`/company-branches/${branch.id}/hard`);
      const updatedBranches = branches.filter(b => b.id !== branch.id);
      setBranches(updatedBranches);
      setFilteredBranches(updatedBranches);
      calculateStats(updatedBranches, companies);
      setError(null);
      setBranchToDelete(null);
    } catch (err) {
      setBranchToDelete(null);
      setError(`Error al eliminar la sucursal: ${err.response?.data?.error || err.response?.data?.message || err.message}`);
      console.error('Error al eliminar sucursal:', err);
    } finally {
      setDeletingBranch(false);
    }
  };

  // Funciones para validación y generación de documentos
  const handleOpenDocValidation = async (branch) => {
    setSelectedBranchForGen(branch);
    setDocValidationDialogOpen(true);
    setValidationLoading(true);
    setValidationData(null);
    try {
      const response = await api.get(`/files/branch/${branch.id}/validate`);
      setValidationData(response.data);
    } catch (err) {
      console.error('Error validando sucursal:', err);
      setError(`Error al validar documentos: ${err.response?.data?.error || err.message}`);
    } finally {
      setValidationLoading(false);
    }
  };

  const handleGenerateDocument = async (branchId, docType) => {
    setGeneratingDoc(docType);
    try {
      const response = await api.post('/files/reports/generate', {
        branchId,
        docType: docType.toUpperCase(),
      });

      const branchName = branches.find(b => String(b.id) === String(branchId))?.branchName || 'la sucursal';
      setSuccess(`${docType.toUpperCase()} generado para ${branchName}. Se guardó en Dropbox.`);
      setGeneratedDocFolderPath(response.data.folderPath || null);
      setTimeout(() => { setSuccess(null); setGeneratedDocFolderPath(null); }, 8000);
    } catch (err) {
      console.error(`Error generando ${docType}:`, err);
      setError(`Error al generar ${docType.toUpperCase()}: ${err.response?.data?.error || err.message}`);
    } finally {
      setGeneratingDoc(null);
    }
  };

  const handleGoToGestionDocumental = () => {
    setSuccess(null);
    navigate('/servicios/documentos', { state: { initialPath: generatedDocFolderPath } });
  };

  const getReadinessIcon = (docData) => {
    if (!docData) return <CircleIcon sx={{ fontSize: 14, color: '#bbb' }} />;
    if (docData.ready) return <CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} />;
    if (docData.completionPercentage >= 70) return <WarningIcon sx={{ fontSize: 14, color: '#ff9800' }} />;
    return <ErrorIcon sx={{ fontSize: 14, color: '#f44336' }} />;
  };

  // Mapea cada sección de campos faltantes (RIC/PIPC) a la pantalla donde se captura.
  // INMUEBLE/OPERATIVA viven en esta misma pantalla (editar la sucursal); el resto vive
  // en otras pantallas del sistema.
  const getSectionAction = (sectionName) => {
    switch (sectionName) {
      case 'Información de la Empresa':
        return { label: 'Ir a Catálogo de Empresas', onClick: () => navigate('/clientes/empresas') };
      case 'Información del Inmueble':
      case 'Información Operativa':
        return {
          label: 'Editar esta sucursal',
          onClick: () => {
            setDocValidationDialogOpen(false);
            if (selectedBranchForGen) handleEditClick(selectedBranchForGen);
          }
        };
      case 'Información del PIPC':
      case 'Tablas Dinámicas':
      case 'Información del Documento':
        return { label: 'Ir a Programas de Servicios', onClick: () => navigate('/servicios/programas') };
      case 'Evaluación de Inspección':
        return { label: 'Ir a Evaluación de Inspección', onClick: () => navigate('/evaluaciones/inspeccion') };
      default:
        return null;
    }
  };

  const renderFieldSection = (sectionName, fields, type) => {
    if (!fields || fields.length === 0) return null;
    const action = getSectionAction(sectionName);
    return (
      <Box key={`${type}-${sectionName}`} sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', textTransform: 'uppercase' }}>
            {sectionName}
          </Typography>
          {action && (
            <Button
              size="small"
              variant="text"
              onClick={action.onClick}
              sx={{ textTransform: 'none', fontSize: '0.72rem', minWidth: 0, py: 0, lineHeight: 1.4 }}
            >
              {action.label} →
            </Button>
          )}
        </Box>
        {fields.map((field, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, my: 0.3 }}>
            <CircleIcon sx={{ fontSize: 8, color: type === 'required' ? '#f44336' : '#ff9800' }} />
            <Typography variant="caption" sx={{ color: type === 'required' ? '#c62828' : '#e65100' }}>
              {field}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Funciones para gestión de documentos
  const handleOpenDocuments = (branch) => {
    setSelectedBranchForDocs({ ...branch, companyName: getCompanyName(branch.companyId) });
    setDocumentsDialogOpen(true);
  };

  const handleDocumentUploadSuccess = (result) => {
    setGeneratedDocFolderPath(null);
    setSuccess(`${result.uploadedCount} documento(s) subido(s) exitosamente`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDocumentUploadError = (message) => {
    setError(message);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box p={3}>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <ReturnToValidationBanner info={returnToValidation} autoReturn onReturn={() => goBackToValidation(navigate, returnToValidation)} />
      {error && !editDialogOpen && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Breadcrumb Navigation */}
      {selectedCompanyName && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={() => navigate('/clientes/empresas')}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Catálogo de Empresas
          </Link>
          <Typography color="text.primary">
            Sucursales - {selectedCompanyName}
          </Typography>
        </Breadcrumbs>
      )}

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
        {selectedCompanyName ? `Sucursales de ${selectedCompanyName}` : 'Gestión de Sucursales'}
      </Typography>

      {/* Cards de Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '120px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ textAlign: 'center', width: '100%' }}>
              <LocationIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Sucursales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: '#e8f5e8', borderLeft: '4px solid #4caf50', height: '120px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#4caf50">Activas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: '#ffebee', borderLeft: '4px solid #f44336', height: '120px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#f44336">Inactivas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.inactive}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3', height: '120px', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#2196f3">Empresas con Sucursales</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {Object.values(stats.byCompany).filter(c => c.count > 0).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Distribución por Empresa */}
      <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Distribución por Empresa
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(stats.byCompany)
            .filter(([_, data]) => data.count > 0)
            .map(([companyId, data]) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={companyId}>
                <Card variant="outlined" sx={{ height: '80px', display: 'flex', alignItems: 'center' }}>
                  <CardContent sx={{ width: '100%', py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                      {data.name}
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                      {data.count} sucursal{data.count !== 1 ? 'es' : ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </Paper>

      {/* Tabla de Sucursales */}
      <Paper elevation={3}>
        <Box p={2}>
          {/* Header con título y botón de actualizar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Listado de Sucursales
            </Typography>
            <IconButton 
              onClick={fetchData}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'rotate(180deg)',
                  transition: 'all 0.3s ease-in-out'
                }
              }}
              title="Actualizar lista de sucursales"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
          
          {/* Botones de Acción */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              onClick={handleOpenCreateDialog}
              sx={{ textTransform: 'none' }}
            >
              + Agregar Sucursal
            </Button>
          </Box>
          
          {/* Filtros */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por Nombre"
                value={filters.branchName}
                onChange={(e) => handleFilterChange('branchName', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Empresa</InputLabel>
                <Select
                  value={filters.companyId}
                  label="Empresa"
                  onChange={(e) => handleFilterChange('companyId', e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.businessName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por Localidad"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.isActive}
                  label="Estado"
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="true">Activas</MenuItem>
                  <MenuItem value="false">Inactivas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        
        {/* Vista jerárquica agrupada por empresa con acordeones colapsables */}
        {filteredBranches.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <StoreIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No se encontraron sucursales
            </Typography>
          </Box>
        ) : (
          // Agrupar sucursales por empresa
          Object.entries(
            filteredBranches.reduce((acc, branch) => {
              const companyId = branch.companyId;
              if (!acc[companyId]) {
                acc[companyId] = [];
              }
              acc[companyId].push(branch);
              return acc;
            }, {})
          ).map(([companyId, companyBranches]) => (
            <Accordion 
              key={companyId}
              expanded={expandedCompanies[companyId] === true}
              onChange={() => setExpandedCompanies(prev => ({
                ...prev,
                [companyId]: !prev[companyId]
              }))}
              sx={{ 
                mb: 2,
                border: '1px solid #e0e0e0',
                '&:before': { display: 'none' },
                boxShadow: 2
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                sx={{
                  bgcolor: '#1976d2',
                  color: 'white',
                  '&:hover': { bgcolor: '#1565c0' },
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }
                }}
              >
                <BusinessIcon sx={{ fontSize: 32 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {getCompanyName(companyId)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {companyBranches.length} {companyBranches.length === 1 ? 'sucursal' : 'sucursales'}
                  </Typography>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ p: 0 }}>
                {/* Tabla de sucursales de esta empresa */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Nombre Sucursal</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Ubicación</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Contacto</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Estado</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {companyBranches.map((branch) => (
                        <TableRow 
                          key={branch.id}
                          hover
                          title="Doble clic para editar"
                          onMouseDown={(e) => { if (e.detail > 1) e.preventDefault(); }}
                          onDoubleClick={(e) => { if (!e.target.closest('button')) handleEditClick(branch); }}
                          sx={{ '&:hover': { bgcolor: '#f9f9f9' }, cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationIcon sx={{ color: '#666', fontSize: 18 }} />
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                {branch.branchName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {branch.city && branch.state ? `${branch.city}, ${branch.state}` : (branch.city || branch.state || 'N/A')}
                            </Typography>
                            {(branch.fullAddress || branch.address) && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {branch.fullAddress || branch.address}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {branch.contactName || 'N/A'}
                            </Typography>
                            {branch.contactEmail && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {branch.contactEmail}
                              </Typography>
                            )}
                            {branch.contactPhone && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {branch.contactPhone}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={branch.isActive ? 'Activa' : 'Inactiva'}
                              color={branch.isActive ? 'success' : 'error'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="primary" onClick={() => handleEditClick(branch)} title="Editar">
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" color="secondary" onClick={() => handleOpenDocuments(branch)} title="Subir Documentos">
                              <CloudUploadIcon />
                            </IconButton>
                            <Tooltip title="Generar RIC / PIPC">
                              <IconButton size="small" sx={{ color: '#6a1b9a' }} onClick={() => handleOpenDocValidation(branch)}>
                                <AssignmentIcon />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(branch)} title="Eliminar">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Paper>

      {/* Dialog para Editar Sucursal */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setError(null); }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3
        }}>
          <BusinessIcon />
          Editar Sucursal
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Alert
            severity="info"
            icon={false}
            sx={{
              m: 3, mb: 2, borderRadius: 2, py: 1.25,
              border: `2px solid ${REQUIRED_FIELD_BORDER}`, bgcolor: REQUIRED_FIELD_BG,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#5c4a1f' }}>
              Los campos marcados con{' '}
              <Box component="span" sx={{ color: REQUIRED_LABEL_COLOR, fontWeight: 800 }}>*</Box>
              {' '}y con la caja en{' '}
              <Box component="span" sx={{ color: REQUIRED_LABEL_COLOR, fontWeight: 800 }}>este tono dorado</Box>
              {' '}son obligatorios. El resto puede completarse posteriormente.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#5c4a1f', mt: 0.75 }}>
              Los campos con el borde en{' '}
              <Box component="span" sx={{ color: '#ed6c02', fontWeight: 800 }}>naranja</Box>
              {' '}están pendientes para generar los documentos (PIPC, reportes). Puedes guardar sin ellos.
            </Typography>
          </Alert>
          <Box sx={{ mx: 3 }}>
          <ReturnToValidationBanner info={returnToValidation} autoReturn
            onReturn={() => goBackToValidation(navigate, returnToValidation)} />
          {entityLock?.locked && (
            <Alert severity={entityLock.canOverride || entityLock.editableWithNotice ? 'info' : 'warning'} sx={{ mb: 2, borderRadius: 2 }}>
              {entityLock.editableWithNotice ? (
                <>
                  <strong>Datos con los que se cotizó (empleados, m² y niveles):</strong> este registro {entityLock.reason}.
                  {' '}Puedes capturarlos o modificarlos; al guardar, el cambio queda anotado en la cotización y se avisa a
                  Ventas, Administración y Coordinación de Operaciones para que revisen si ajustan el costo.
                </>
              ) : (
                <>
                  <strong>Datos con los que se cotizó bloqueados (empleados, m² y niveles):</strong> este registro {entityLock.reason}.
                  {' '}Esos 4 datos no se pueden modificar hasta que se cierre, para que
                  la cotización, la orden de trabajo y los documentos coincidan.
                  {entityLock.canOverride && ' Como administrador sí puedes corregirlos.'}
                </>
              )}
            </Alert>
          )}
          </Box>

          {/* Sección de Información General */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              color: '#000000', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <LocationIcon />
              Información General de la Sucursal
            </Typography>
            <Grid container spacing={3}>
              {selectedCompanyId && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Empresa"
                    value={selectedCompanyName || getCompanyName(selectedCompanyId)}
                    variant="outlined"
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                      '& .MuiInputLabel-root': {
                        color: '#000000',
                      },
                    }}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Nombre de la Sucursal')}
                  value={editBranch.branchName}
                  inputProps={{ maxLength: FIELD_LIMITS.branchName }}
                  onChange={(e) => setEditBranch({ ...editBranch, branchName: e.target.value })}
                  variant="outlined"
                  sx={requiredSx(isEmpty(editBranch.branchName))}
                />
                <CaseTypoHint value={editBranch.branchName}
                  onFix={(fixed) => setEditBranch(prev => ({ ...prev, branchName: fixed }))} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl fullWidth>
                    <InputLabel id="business-sector-label-edit" shrink={!!editBranch.businessSector} sx={{ color: isEmpty(editBranch.businessSector) ? '#ed6c02' : '#000000' }}>
                      Giro de la Sucursal
                    </InputLabel>
                    <Select
                      labelId="business-sector-label-edit"
                      id="field-GIRO_EMPRESA"
                      value={editBranch.businessSector || ''}
                      onChange={(e) => setEditBranch({ ...editBranch, businessSector: e.target.value })}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: '#999' }}>Seleccione el giro</span>;
                        }
                        const sector = businessSectors.find(s => s.id === selected);
                        return sector ? sector.name : selected;
                      }}
                      sx={warnSelectSx(isEmpty(editBranch.businessSector))}
                    >
                      {businessSectors.map((sector) => (
                        <MenuItem key={sector.id} value={sector.id}>
                          {sector.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    onClick={() => setSectorModalOpen(true)}
                    sx={{
                      mt: 0.5,
                      bgcolor: '#1976d2',
                      color: 'white',
                      '&:hover': {
                        bgcolor: '#1565c0',
                      },
                      width: 40,
                      height: 40,
                    }}
                    title="Agregar nuevo sector empresarial"
                  >
                    +
                  </IconButton>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: isEmpty(editBranch.companyId) ? REQUIRED_LABEL_COLOR : '#000000' }}>{requiredLabel('Empresa')}</InputLabel>
                  <Select
                    value={editBranch.companyId}
                    label="Empresa *"
                    onChange={(e) => setEditBranch({ ...editBranch, companyId: e.target.value })}
                    sx={requiredSelectSx(isEmpty(editBranch.companyId))}
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ color: '#1976d2' }} />
                          {company.businessName}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Dirección */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              color: '#000000', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <LocationIcon />
              Información de Dirección
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Calle')}
                  id="field-DIRECCION_FISICA_SUCURSAL"
                  value={editBranch.street || ''}
                  onChange={(e) => handleTextChange('street', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.street }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.street)}
                  helperText={createAddressErrors.street || ''}
                  sx={requiredSx(isEmpty(editBranch.street) && !createAddressErrors.street)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Número Exterior')}
                  value={editBranch.exteriorNumber || ''}
                  onChange={(e) => handleAlphanumericChange('exteriorNumber', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.exteriorNumber }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.exteriorNumber)}
                  helperText={createAddressErrors.exteriorNumber || ''}
                  sx={requiredSx(isEmpty(editBranch.exteriorNumber) && !createAddressErrors.exteriorNumber)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Número Interior"
                  value={editBranch.interiorNumber || ''}
                  onChange={(e) => handleAlphanumericChange('interiorNumber', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.interiorNumber }}
                  variant="outlined"
                  helperText="Opcional"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#1976d2' },
                    },
                    '& .MuiInputLabel-root': { color: '#000000' },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Colonia/Barrio')}
                  value={editBranch.neighborhood || ''}
                  onChange={(e) => handleTextChange('neighborhood', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.neighborhood }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.neighborhood)}
                  helperText={createAddressErrors.neighborhood || ''}
                  sx={requiredSx(isEmpty(editBranch.neighborhood) && !createAddressErrors.neighborhood)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Municipio/Delegación')}
                  id="field-MUNICIPIO"
                  value={editBranch.municipality || ''}
                  onChange={(e) => handleTextChange('municipality', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.municipality }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.municipality)}
                  helperText={createAddressErrors.municipality || ''}
                  sx={requiredSx(isEmpty(editBranch.municipality) && !createAddressErrors.municipality)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Localidad')}
                  value={editBranch.city || ''}
                  onChange={(e) => handleTextChange('city', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.locality }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.city)}
                  helperText={createAddressErrors.city || ''}
                  sx={requiredSx(isEmpty(editBranch.city) && !createAddressErrors.city)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel sx={{ color: isEmpty(editBranch.state) ? REQUIRED_LABEL_COLOR : '#000000' }}>{requiredLabel('Estado')}</InputLabel>
                  <Select
                    value={editBranch.state || ''}
                    onChange={(e) => setEditBranch({ ...editBranch, state: e.target.value })}
                    label="Estado *"
                    sx={requiredSelectSx(isEmpty(editBranch.state))}
                  >
                    <MenuItem value=""><em>Selecciona un estado</em></MenuItem>
                    {ESTADOS_MEXICO.map((estado) => (
                      <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                    ))}
                  </Select>
                  {!!createAddressErrors.state && (
                    <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5 }}>
                      {createAddressErrors.state}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Código Postal')}
                  value={editBranch.postalCode || ''}
                  onChange={(e) => handleNumericChange('postalCode', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.postalCode }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.postalCode) || !postalCodeValidation.isValid}
                  helperText={createAddressErrors.postalCode || (!postalCodeValidation.isValid ? "Código postal incorrecto" : "")}
                  sx={(() => {
                    const hasError = Boolean(createAddressErrors.postalCode) || !postalCodeValidation.isValid;
                    const warn = isEmpty(editBranch.postalCode) && !hasError;
                    const borderColor = hasError ? '#f44336' : warn ? '#ed6c02' : REQUIRED_FIELD_BORDER;
                    const hoverColor = hasError ? '#f44336' : warn ? '#ed6c02' : REQUIRED_LABEL_COLOR;
                    return {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: (hasError || warn) ? undefined : REQUIRED_FIELD_BG,
                        '&:hover fieldset': { borderColor: hoverColor },
                        '& fieldset': { borderColor },
                      },
                      '& .MuiInputLabel-root': { color: hasError ? '#f44336' : warn ? '#ed6c02' : '#000000' },
                      '& .MuiFormHelperText-root': { color: '#f44336' },
                    };
                  })()}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('País')}
                  value={editBranch.country || ''}
                  inputProps={{ maxLength: FIELD_LIMITS.country }}
                  onChange={(e) => {
                    setEditBranch({ ...editBranch, country: e.target.value });
                    setCreateAddressErrors(prev => ({ ...prev, country: '' }));
                  }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.country)}
                  helperText={createAddressErrors.country || ''}
                  sx={requiredSx(isEmpty(editBranch.country) && !createAddressErrors.country)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Georeferencia')}
                  id="field-GEOREFERENCIA"
                  value={editBranch.geolocation}
                  inputProps={{ maxLength: FIELD_LIMITS.geolocation }}
                  onChange={(e) => handleGeolocationChange(e.target.value)}
                  variant="outlined"
                  placeholder="Ej: 19.432608, -99.133209"
                  error={Boolean(createAddressErrors.geolocation) || !geolocationValidation.isValid}
                  helperText={createAddressErrors.geolocation || (!geolocationValidation.isValid ? geolocationValidation.message : "Coordenadas geográficas (latitud, longitud)")}
                  sx={(() => {
                    const hasError = Boolean(createAddressErrors.geolocation) || !geolocationValidation.isValid;
                    const warn = isEmpty(editBranch.geolocation) && !hasError;
                    const borderColor = hasError ? '#f44336' : warn ? '#ed6c02' : REQUIRED_FIELD_BORDER;
                    const hoverColor = hasError ? '#f44336' : warn ? '#ed6c02' : REQUIRED_LABEL_COLOR;
                    return {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: (hasError || warn) ? undefined : REQUIRED_FIELD_BG,
                        '&:hover fieldset': { borderColor: hoverColor },
                        '& fieldset': { borderColor },
                      },
                      '& .MuiInputLabel-root': { color: hasError ? '#f44336' : warn ? '#ed6c02' : '#000000' },
                      '& .MuiFormHelperText-root': { color: hasError ? '#f44336' : 'inherit' },
                    };
                  })()}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Contacto */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{
              mb: 2,
              color: '#000000',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <PersonIcon />
              Información de Contacto
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <Autocomplete
                      freeSolo
                      fullWidth
                      options={COMMON_CONTACT_TITLES}
                      value={editBranch.contactTitle}
                      onChange={(e, newValue) => setEditBranch({ ...editBranch, contactTitle: (newValue || '').slice(0, 10) })}
                      onInputChange={(e, newValue) => setEditBranch({ ...editBranch, contactTitle: newValue.slice(0, 10) })}
                      renderInput={(params) => (
                        <TextField {...params} label="Título" variant="outlined" placeholder="Lic., Ing., Dr." />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      label="Nombre(s)"
                      id="field-RESPONSABLE_SUCURSAL"
                      value={editBranch.contactFirstName}
                      onChange={(e) => setEditBranch({ ...editBranch, contactFirstName: e.target.value })}
                      onBlur={(e) => e.target.value.trim() && setEditBranch(prev => ({ ...prev, contactFirstName: capitalizePersonName(e.target.value) }))}
                      variant="outlined"
                      sx={warnSx(isEmpty(editBranch.contactFirstName))}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3.5 }}>
                    <TextField
                      fullWidth
                      label="Apellido Paterno"
                      value={editBranch.contactPaternalSurname}
                      onChange={(e) => setEditBranch({ ...editBranch, contactPaternalSurname: e.target.value })}
                      onBlur={(e) => e.target.value.trim() && setEditBranch(prev => ({ ...prev, contactPaternalSurname: capitalizePersonName(e.target.value) }))}
                      variant="outlined"
                      sx={warnSx(isEmpty(editBranch.contactPaternalSurname))}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3.5 }}>
                    <TextField
                      fullWidth
                      label="Apellido Materno"
                      value={editBranch.contactMaternalSurname}
                      onChange={(e) => setEditBranch({ ...editBranch, contactMaternalSurname: e.target.value })}
                      onBlur={(e) => e.target.value.trim() && setEditBranch(prev => ({ ...prev, contactMaternalSurname: capitalizePersonName(e.target.value) }))}
                      variant="outlined"
                      sx={warnSx(isEmpty(editBranch.contactMaternalSurname))}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email de Contacto"
                  type="email"
                  id="field-EMAIL_SUCURSAL"
                  value={editBranch.contactEmail}
                  inputProps={{ maxLength: FIELD_LIMITS.email }}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  error={!emailValidation.isValid}
                  helperText={
                    !emailValidation.isValid
                      ? emailValidation.message
                      : emailValidation.isValid && editBranch.contactEmail
                        ? emailValidation.message
                        : 'Formato: usuario@empresa.com'
                  }
                  variant="outlined"
                  sx={warnSx(isEmpty(editBranch.contactEmail) && emailValidation.isValid)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Teléfono de Contacto"
                  id="field-TELEFONO_SUCURSAL"
                  value={editBranch.contactPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  error={!phoneValidation.isValid}
                  helperText={
                    !phoneValidation.isValid
                      ? phoneValidation.message
                      : phoneValidation.isValid && editBranch.contactPhone
                        ? phoneValidation.message
                        : 'Máximo 10 dígitos'
                  }
                  inputProps={{ maxLength: FIELD_LIMITS.phone }}
                  variant="outlined"
                  sx={warnSx(isEmpty(editBranch.contactPhone) && phoneValidation.isValid)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Compañía de Seguros"
                  id="field-COMPANIA_SEGUROS"
                  value={editBranch.insuranceCompany || ''}
                  onChange={(e) => setEditBranch(prev => ({ ...prev, insuranceCompany: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.insuranceCompany }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Número de Póliza"
                  id="field-NUMERO_POLIZA"
                  value={editBranch.insurancePolicyNumber || ''}
                  onChange={(e) => setEditBranch(prev => ({ ...prev, insurancePolicyNumber: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.insurancePolicyNumber }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Vigencia de la Póliza"
                  id="field-VIGENCIA_POLIZA"
                  value={editBranch.insurancePolicyValidity || ''}
                  onChange={(e) => setEditBranch(prev => ({ ...prev, insurancePolicyValidity: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Información del Inmueble */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{
              mb: 2,
              color: '#000000',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <BusinessIcon />
              Información del Inmueble
              {lockFields && <Chip size="small" label={`🔒 Bloqueado: ${entityLock.reason}`} sx={{ ml: 1, fontWeight: 500, bgcolor: '#fff3e0', color: '#e65100' }} />}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Área de Construcción (m²)"
                  disabled={lockFields}
                  id="field-AREA_CONSTRUCCION"
                  value={editBranch.constructionArea}
                  onChange={(e) => handlePropertyFieldChange('constructionArea', e.target.value)}
                  variant="outlined"
                  helperText={propertyFieldHelperText('constructionArea')}
                  inputProps={{ maxLength: FIELD_LIMITS.constructionArea.digits, inputMode: 'decimal' }}
                  sx={warnSx(isEmpty(editBranch.constructionArea))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Área de Terreno (m²)"
                  disabled={lockFields}
                  id="field-AREA_TERRENO"
                  value={editBranch.landArea}
                  onChange={(e) => handlePropertyFieldChange('landArea', e.target.value)}
                  variant="outlined"
                  helperText={propertyFieldHelperText('landArea')}
                  inputProps={{ maxLength: FIELD_LIMITS.landArea.digits, inputMode: 'decimal' }}
                  sx={warnSx(isEmpty(editBranch.landArea))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: isEmpty(editBranch.constructionLevels) ? '#ed6c02' : '#000000' }}>Número de Niveles</InputLabel>
                  <Select
                    id="field-NIVELES_CONSTRUCCION"
                    value={editBranch.constructionLevels}
                    label="Número de Niveles"
                    disabled={lockFields}
                    onChange={(e) => setEditBranch({ ...editBranch, constructionLevels: e.target.value })}
                    sx={warnSelectSx(isEmpty(editBranch.constructionLevels))}
                  >
                    <MenuItem value="">Seleccionar</MenuItem>
                    {[...Array(10)].map((_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>{i + 1} {i + 1 === 1 ? 'Nivel' : 'Niveles'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#000000' }}>Régimen de Propiedad</InputLabel>
                  <Select
                    id="field-REGIMEN_PROPIEDAD"
                    value={editBranch.propertyRegime || 'PROPIO'}
                    label="Régimen de Propiedad"
                    onChange={(e) => setEditBranch({ ...editBranch, propertyRegime: e.target.value })}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                    }}
                  >
                    <MenuItem value="PROPIO">Propio</MenuItem>
                    <MenuItem value="ARRENDADO">Arrendado</MenuItem>
                    <MenuItem value="COMODATO">Comodato</MenuItem>
                    <MenuItem value="OTRO">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Información Operativa */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{
              mb: 2,
              color: '#000000',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <StoreIcon />
              Información Operativa
              {lockFields && <Chip size="small" label={`🔒 Bloqueado: ${entityLock.reason}`} sx={{ ml: 1, fontWeight: 500, bgcolor: '#fff3e0', color: '#e65100' }} />}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Número de Empleados"
                  disabled={lockFields}
                  id="field-NUMERO_EMPLEADOS"
                  value={editBranch.employeesCount}
                  onChange={(e) => handlePropertyFieldChange('employeesCount', e.target.value)}
                  variant="outlined"
                  helperText={propertyFieldHelperText('employeesCount')}
                  inputProps={{ maxLength: FIELD_LIMITS.employeesCount.digits, inputMode: 'numeric' }}
                  sx={warnSx(isEmpty(editBranch.employeesCount))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Aforo Máximo"
                  id="field-CAPACIDAD_MAXIMA"
                  value={editBranch.maximumCapacity}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    if (numericValue.length <= 5) {
                      setEditBranch({ ...editBranch, maximumCapacity: numericValue });
                    }
                  }}
                  variant="outlined"
                  helperText="Máximo 5 dígitos"
                  inputProps={{ maxLength: FIELD_LIMITS.maximumCapacity.digits, inputMode: 'numeric' }}
                  sx={warnSx(isEmpty(editBranch.maximumCapacity))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Población Flotante"
                  id="field-POBLACION_FLOTANTE"
                  value={editBranch.floatingPopulation}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    if (numericValue.length <= 5) {
                      setEditBranch({ ...editBranch, floatingPopulation: numericValue });
                    }
                  }}
                  variant="outlined"
                  helperText="Máximo 5 dígitos"
                  inputProps={{ maxLength: FIELD_LIMITS.floatingPopulation.digits, inputMode: 'numeric' }}
                  sx={warnSx(isEmpty(editBranch.floatingPopulation))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Operativo — Inicio"
                  id="field-HORARIO_TRABAJO"
                  value={editBranch.operatingHoursStart}
                  onChange={(e) => setEditBranch({ ...editBranch, operatingHoursStart: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={warnSx(isEmpty(editBranch.operatingHoursStart))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Operativo — Fin"
                  value={editBranch.operatingHoursEnd}
                  onChange={(e) => setEditBranch({ ...editBranch, operatingHoursEnd: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={warnSx(isEmpty(editBranch.operatingHoursEnd))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Administrativo — Inicio"
                  id="field-HORARIO_ADMINISTRATIVO"
                  value={editBranch.administrativeHoursStart}
                  onChange={(e) => setEditBranch({ ...editBranch, administrativeHoursStart: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  placeholder="HH:mm"
                  helperText="Horario de atención administrativa"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Administrativo — Fin"
                  value={editBranch.administrativeHoursEnd}
                  onChange={(e) => setEditBranch({ ...editBranch, administrativeHoursEnd: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  placeholder="HH:mm"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Actividades Realizadas"
                  id="field-ACTIVIDADES_PRINCIPALES"
                  value={editBranch.mainActivities}
                  inputProps={{ maxLength: FIELD_LIMITS.mainActivities }}
                  onChange={(e) => setEditBranch({ ...editBranch, mainActivities: e.target.value })}
                  variant="outlined"
                  multiline
                  rows={3}
                  helperText="Descripción de las actividades principales realizadas en la sucursal"
                  sx={warnSx(isEmpty(editBranch.mainActivities))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#000000' }}>Estado</InputLabel>
                  <Select
                    value={editBranch.isActive}
                    label="Estado"
                    onChange={(e) => setEditBranch({ ...editBranch, isActive: e.target.value })}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                    }}
                  >
                    <MenuItem value={true}>
                      <Chip label="Activa" color="success" size="small" />
                    </MenuItem>
                    <MenuItem value={false}>
                      <Chip label="Inactiva" color="error" size="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Documentos (solo para edición) */}
          {selectedBranch && (
            <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
              <Typography variant="h6" sx={{ 
                mb: 2, 
                color: '#000000', 
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <DescriptionIcon />
                Documentos de la Sucursal
              </Typography>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => handleOpenDocuments(selectedBranch)}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                  }
                }}
              >
                Gestionar Documentos
              </Button>
            </Paper>
          )}

          {/* Sección de Foto del Inmueble (solo para edición) */}
          {selectedBranch && (
            <Paper id="field-IMAGEN_INMUEBLE" elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
              <Typography variant="h6" sx={{
                mb: 2,
                color: '#000000',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <CloudUploadIcon />
                Fotografía del Inmueble
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Al subir el archivo se guarda de inmediato — no necesita presionar "Guardar Cambios" para que se registre.
              </Typography>

              {selectedBranch.hasPropertyImage && branchPropertyImagePreviewLoading && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Cargando previsualización de la fotografía guardada…
                  </Typography>
                </Box>
              )}

              {selectedBranch.hasPropertyImage && !branchPropertyImagePreviewLoading && !branchPropertyImagePreview && !branchPropertyImageError && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Fotografía registrada. Puede actualizarla subiendo un nuevo archivo.
                  </Typography>
                </Box>
              )}

              {branchPropertyImagePreview && (
                <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, display: 'inline-block' }}>
                  <img src={branchPropertyImagePreview} alt="Preview fotografía del inmueble" style={{ maxHeight: 160, maxWidth: 320, objectFit: 'contain' }} />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  ref={branchPropertyImageInputRef}
                  type="file"
                  accept={JPG_PNG_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={handleBranchPropertyImageSelect}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => branchPropertyImageInputRef.current?.click()}
                  sx={{ textTransform: 'none' }}
                >
                  {branchPropertyImageFile ? branchPropertyImageFile.name : 'Seleccionar archivo .jpg o .png'}
                </Button>
                {branchPropertyImageFile && (
                  <Button
                    variant="contained"
                    onClick={handleBranchPropertyImageUpload}
                    disabled={branchPropertyImageUploading}
                    startIcon={branchPropertyImageUploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    {branchPropertyImageUploading ? 'Subiendo...' : 'Subir Fotografía'}
                  </Button>
                )}
              </Box>

              {branchPropertyImageSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Fotografía subida correctamente.
                </Alert>
              )}
              {branchPropertyImageError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {branchPropertyImageError}
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Formato: JPG o PNG · Tamaño máximo: 8 MB
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
          {error && (
            <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ flex: 1, mr: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <Button 
            onClick={() => {
              setEditDialogOpen(false);
              setError(null);
              resetForm();
            }}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              }
            }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!branchToDelete} onClose={() => !deletingBranch && setBranchToDelete(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 700 }}>
          Eliminar sucursal permanentemente
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Vas a eliminar la sucursal <strong>{branchToDelete?.branchName}</strong> y su información en CONEM:
          </Typography>
          <Typography component="ul" sx={{ mb: 2, pl: 3 }}>
            <li>Sus datos generales, de dirección y de contacto</li>
            <li>Los registros de sus documentos subidos y de los documentos de cotización ligados a ella</li>
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Sus carpetas y archivos en Dropbox <strong>NO se eliminan</strong>. Si ya no los necesitas, bórralos directamente en Dropbox.
          </Alert>
          <Typography sx={{ fontWeight: 700 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBranchToDelete(null)} disabled={deletingBranch}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteBranch} variant="contained" color="error" disabled={deletingBranch}>
            {deletingBranch ? 'Eliminando...' : 'Eliminar definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Crear Sucursal */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCreateCloseAttempt}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.3rem',
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Crear Sucursal
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, maxHeight: '75vh' }} ref={createDialogContentRef}>
          {createFormError && (
            <Alert
              severity="error"
              sx={{
                m: 3,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontWeight: 'bold'
                }
              }}
            >
              {createFormError}
            </Alert>
          )}
          <Alert
            severity="info"
            icon={false}
            sx={{
              m: 3, mb: 2, borderRadius: 2, py: 1.25,
              border: `2px solid ${REQUIRED_FIELD_BORDER}`, bgcolor: REQUIRED_FIELD_BG,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#5c4a1f' }}>
              Los campos marcados con{' '}
              <Box component="span" sx={{ color: REQUIRED_LABEL_COLOR, fontWeight: 800 }}>*</Box>
              {' '}y con la caja en{' '}
              <Box component="span" sx={{ color: REQUIRED_LABEL_COLOR, fontWeight: 800 }}>este tono dorado</Box>
              {' '}son obligatorios. El resto puede completarse posteriormente.
            </Typography>
          </Alert>

          {/* Sección General */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              color: '#000000', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <StoreIcon />
              Información General
            </Typography>
            <Grid container spacing={3}>
              {/* 1. Empresa (primero) */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="company-label-create"
                    sx={{ color: isEmpty(editBranch.companyId) ? REQUIRED_LABEL_COLOR : '#000000' }}
                  >
                    {requiredLabel('Empresa')}
                  </InputLabel>
                  <Select
                    labelId="company-label-create"
                    value={editBranch.companyId || ''}
                    label="Empresa *"
                    onChange={(e) => {
                      const newCompanyId = e.target.value;
                      const newCompany = companies.find(c => c.id === parseInt(newCompanyId));

                      if (newCompany) {
                        const prefilledContactTitle = newCompany.corporateContactTitle || '';
                        const prefilledContactFirstName = newCompany.corporateContactFirstName || '';
                        const prefilledContactPaternalSurname = newCompany.corporateContactPaternalSurname || '';
                        const prefilledContactMaternalSurname = newCompany.corporateContactMaternalSurname || '';
                        const prefilledContactEmail = newCompany.corporateContactEmail || '';
                        const prefilledContactPhone = newCompany.corporateContactPhone || '';
                        const sameInsurance = !!newCompany.insuranceSameForBranches;

                        setEditBranch(prev => ({
                          ...prev,
                          companyId: newCompanyId,
                          contactTitle: (prev.contactTitle || '').trim() ? prev.contactTitle : prefilledContactTitle,
                          contactFirstName: (prev.contactFirstName || '').trim() ? prev.contactFirstName : prefilledContactFirstName,
                          contactPaternalSurname: (prev.contactPaternalSurname || '').trim() ? prev.contactPaternalSurname : prefilledContactPaternalSurname,
                          contactMaternalSurname: (prev.contactMaternalSurname || '').trim() ? prev.contactMaternalSurname : prefilledContactMaternalSurname,
                          contactEmail: (prev.contactEmail || '').trim() ? prev.contactEmail : prefilledContactEmail,
                          contactPhone: (prev.contactPhone || '').trim() ? prev.contactPhone : prefilledContactPhone,
                          insuranceCompany: sameInsurance ? (newCompany.insuranceCompany || '') : prev.insuranceCompany,
                          insurancePolicyNumber: sameInsurance ? (newCompany.insurancePolicyNumber || '') : prev.insurancePolicyNumber,
                          insurancePolicyValidity: sameInsurance ? (newCompany.insurancePolicyValidity || '') : prev.insurancePolicyValidity,
                          // NO heredar dirección de la empresa matriz
                          // Mantener los campos de dirección como están
                        }));

                        setEmailValidation(validateEmailFormat((editBranch.contactEmail || '').trim() ? editBranch.contactEmail : prefilledContactEmail));
                        setPhoneValidation(validatePhoneFormat((editBranch.contactPhone || '').trim() ? editBranch.contactPhone : prefilledContactPhone));
                        return;
                      }

                      setEditBranch({ ...editBranch, companyId: newCompanyId });
                    }}
                    disabled={!!selectedCompanyId}
                    sx={requiredSelectSx(isEmpty(editBranch.companyId))}
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ color: '#1976d2' }} />
                          {company.businessName}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* 2. Nombre de la Sucursal (segundo) */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Nombre de la Sucursal')}
                  value={editBranch.branchName}
                  inputProps={{ maxLength: FIELD_LIMITS.branchName }}
                  onChange={(e) => {
                    setEditBranch({ ...editBranch, branchName: e.target.value });
                    // Limpiar validación cuando el usuario empieza a escribir
                    if (e.target.value.trim() !== '') {
                      setBranchNameValidation({ isValid: true, message: '' });
                    }
                  }}
                  variant="outlined"
                  error={!branchNameValidation.isValid}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: !branchNameValidation.isValid ? undefined : REQUIRED_FIELD_BG,
                      '& fieldset': { borderColor: !branchNameValidation.isValid ? '#f44336' : REQUIRED_FIELD_BORDER },
                      '&:hover fieldset': { borderColor: !branchNameValidation.isValid ? '#f44336' : REQUIRED_LABEL_COLOR },
                    },
                    '& .MuiInputLabel-root': { color: !branchNameValidation.isValid ? '#f44336' : '#000000' },
                  }}
                />
                <CaseTypoHint value={editBranch.branchName}
                  onFix={(fixed) => setEditBranch(prev => ({ ...prev, branchName: fixed }))} />
                {!branchNameValidation.isValid && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#f44336', 
                      mt: 0.5, 
                      display: 'block',
                      fontWeight: 'bold'
                    }}
                  >
                    {branchNameValidation.message}
                  </Typography>
                )}
              </Grid>
              
              {/* 3. Giro de la Sucursal (tercero) */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl fullWidth>
                    <InputLabel id="business-sector-label-create" sx={{ color: '#000000' }}>
                      Giro de la Sucursal
                    </InputLabel>
                    <Select
                      labelId="business-sector-label-create"
                      value={editBranch.businessSector || ''}
                      label="Giro de la Sucursal"
                      onChange={(e) => setEditBranch({ ...editBranch, businessSector: e.target.value })}
                      sx={{
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                        },
                      }}
                    >
                      {businessSectors.map((sector) => (
                        <MenuItem key={sector.id} value={sector.id}>
                          {sector.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    onClick={() => setSectorModalOpen(true)}
                    sx={{
                      mt: 0.5,
                      bgcolor: '#1976d2',
                      color: 'white',
                      '&:hover': {
                        bgcolor: '#1565c0',
                      },
                      width: 40,
                      height: 40,
                    }}
                    title="Agregar nuevo sector empresarial"
                  >
                    +
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Dirección */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              color: '#000000', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <LocationIcon />
              Información de Dirección
            </Typography>
            <Grid container spacing={3}>
             
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Calle')}
                  value={editBranch.street || ''}
                  onChange={(e) => handleTextChange('street', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.street }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.street)}
                  helperText={createAddressErrors.street || ''}
                  sx={requiredSx(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Número Exterior')}
                  value={editBranch.exteriorNumber || ''}
                  onChange={(e) => handleAlphanumericChange('exteriorNumber', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.exteriorNumber }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.exteriorNumber)}
                  helperText={createAddressErrors.exteriorNumber || ''}
                  sx={requiredSx(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Número Interior"
                  value={editBranch.interiorNumber || ''}
                  onChange={(e) => handleAlphanumericChange('interiorNumber', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.interiorNumber }}
                  variant="outlined"
                  helperText="Opcional"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: editBranch.interiorNumber ? '#f5f5f5' : 'transparent',
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Colonia/Barrio')}
                  value={editBranch.neighborhood || ''}
                  onChange={(e) => handleTextChange('neighborhood', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.neighborhood }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.neighborhood)}
                  helperText={createAddressErrors.neighborhood || ''}
                  sx={requiredSx(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Municipio/Delegación')}
                  value={editBranch.municipality || ''}
                  onChange={(e) => handleTextChange('municipality', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.municipality }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.municipality)}
                  helperText={createAddressErrors.municipality || ''}
                  sx={requiredSx(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Localidad')}
                  value={editBranch.city || ''}
                  onChange={(e) => handleTextChange('city', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.locality }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.city)}
                  helperText={createAddressErrors.city || ''}
                  sx={requiredSx(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth variant="outlined" error={Boolean(createAddressErrors.state)}>
                  <InputLabel sx={{ color: '#000000' }}>{requiredLabel('Estado')}</InputLabel>
                  <Select
                    value={editBranch.state || ''}
                    onChange={(e) => {
                      setEditBranch({ ...editBranch, state: e.target.value });
                      setCreateAddressErrors(prev => ({ ...prev, state: '' }));
                    }}
                    label="Estado *"
                    sx={requiredSelectSx(false)}
                  >
                    <MenuItem value=""><em>Selecciona un estado</em></MenuItem>
                    {ESTADOS_MEXICO.map((estado) => (
                      <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                    ))}
                  </Select>
                  {!!createAddressErrors.state && (
                    <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5 }}>
                      {createAddressErrors.state}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Código Postal')}
                  value={editBranch.postalCode || ''}
                  onChange={(e) => handleNumericChange('postalCode', e.target.value)}
                  inputProps={{ maxLength: FIELD_LIMITS.postalCode }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.postalCode) || !postalCodeValidation.isValid}
                  helperText={createAddressErrors.postalCode || (!postalCodeValidation.isValid ? "Código postal incorrecto" : "")}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: !postalCodeValidation.isValid ? undefined : REQUIRED_FIELD_BG,
                      '&:hover fieldset': {
                        borderColor: !postalCodeValidation.isValid ? '#f44336' : REQUIRED_LABEL_COLOR,
                      },
                      '& fieldset': {
                        borderColor: !postalCodeValidation.isValid ? '#f44336' : REQUIRED_FIELD_BORDER,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: !postalCodeValidation.isValid ? '#f44336' : '#000000',
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#f44336',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('País')}
                  value={editBranch.country || ''}
                  inputProps={{ maxLength: FIELD_LIMITS.country }}
                  onChange={(e) => {
                    setEditBranch({ ...editBranch, country: e.target.value });
                    setCreateAddressErrors(prev => ({ ...prev, country: '' }));
                  }}
                  variant="outlined"
                  error={Boolean(createAddressErrors.country)}
                  helperText={createAddressErrors.country || ''}
                  sx={requiredSx(false)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={requiredLabel('Georeferencia')}
                  value={editBranch.geolocation}
                  inputProps={{ maxLength: FIELD_LIMITS.geolocation }}
                  onChange={(e) => handleGeolocationChange(e.target.value)}
                  variant="outlined"
                  placeholder="Ej: 19.432608, -99.133209"
                  error={Boolean(createAddressErrors.geolocation) || !geolocationValidation.isValid}
                  helperText={createAddressErrors.geolocation || (!geolocationValidation.isValid ? geolocationValidation.message : "Coordenadas geográficas (latitud, longitud)")}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: !geolocationValidation.isValid ? undefined : REQUIRED_FIELD_BG,
                      '&:hover fieldset': {
                        borderColor: !geolocationValidation.isValid ? '#f44336' : REQUIRED_LABEL_COLOR,
                      },
                      '& fieldset': {
                        borderColor: !geolocationValidation.isValid ? '#f44336' : REQUIRED_FIELD_BORDER,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: !geolocationValidation.isValid ? '#f44336' : '#000000',
                    },
                    '& .MuiFormHelperText-root': {
                      color: !geolocationValidation.isValid ? '#f44336' : 'inherit',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Contacto */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{
              mb: 2,
              color: '#000000',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <PersonIcon />
              Información de Contacto
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <Autocomplete
                      freeSolo
                      fullWidth
                      options={COMMON_CONTACT_TITLES}
                      value={editBranch.contactTitle}
                      onChange={(e, newValue) => setEditBranch({ ...editBranch, contactTitle: (newValue || '').slice(0, 10) })}
                      onInputChange={(e, newValue) => setEditBranch({ ...editBranch, contactTitle: newValue.slice(0, 10) })}
                      renderInput={(params) => (
                        <TextField {...params} label="Título" variant="outlined" placeholder="Lic., Ing., Dr." />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      label="Nombre(s)"
                      value={editBranch.contactFirstName}
                      onChange={(e) => setEditBranch({ ...editBranch, contactFirstName: e.target.value })}
                      onBlur={(e) => e.target.value.trim() && setEditBranch(prev => ({ ...prev, contactFirstName: capitalizePersonName(e.target.value) }))}
                      variant="outlined"
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3.5 }}>
                    <TextField
                      fullWidth
                      label="Apellido Paterno"
                      value={editBranch.contactPaternalSurname}
                      onChange={(e) => setEditBranch({ ...editBranch, contactPaternalSurname: e.target.value })}
                      onBlur={(e) => e.target.value.trim() && setEditBranch(prev => ({ ...prev, contactPaternalSurname: capitalizePersonName(e.target.value) }))}
                      variant="outlined"
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3.5 }}>
                    <TextField
                      fullWidth
                      label="Apellido Materno"
                      value={editBranch.contactMaternalSurname}
                      onChange={(e) => setEditBranch({ ...editBranch, contactMaternalSurname: e.target.value })}
                      onBlur={(e) => e.target.value.trim() && setEditBranch(prev => ({ ...prev, contactMaternalSurname: capitalizePersonName(e.target.value) }))}
                      variant="outlined"
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email de Contacto"
                  type="email"
                  value={editBranch.contactEmail}
                  inputProps={{ maxLength: FIELD_LIMITS.email }}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  error={!emailValidation.isValid}
                  helperText={
                    !emailValidation.isValid 
                      ? emailValidation.message 
                      : emailValidation.isValid && editBranch.contactEmail 
                        ? emailValidation.message 
                        : 'Formato: usuario@empresa.com'
                  }
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Teléfono de Contacto"
                  value={editBranch.contactPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  error={!phoneValidation.isValid}
                  helperText={
                    !phoneValidation.isValid 
                      ? phoneValidation.message 
                      : phoneValidation.isValid && editBranch.contactPhone 
                        ? phoneValidation.message 
                        : 'Máximo 10 dígitos'
                  }
                  inputProps={{ maxLength: FIELD_LIMITS.phone }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Compañía de Seguros"
                  value={editBranch.insuranceCompany || ''}
                  onChange={(e) => setEditBranch(prev => ({ ...prev, insuranceCompany: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.insuranceCompany }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Número de Póliza"
                  value={editBranch.insurancePolicyNumber || ''}
                  onChange={(e) => setEditBranch(prev => ({ ...prev, insurancePolicyNumber: e.target.value }))}
                  inputProps={{ maxLength: FIELD_LIMITS.insurancePolicyNumber }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Vigencia de la Póliza"
                  value={editBranch.insurancePolicyValidity || ''}
                  onChange={(e) => setEditBranch(prev => ({ ...prev, insurancePolicyValidity: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Información del Inmueble */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{
              mb: 2,
              color: '#000000', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <BusinessIcon />
              Información del Inmueble
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Área de Construcción (m²)"
                  value={editBranch.constructionArea}
                  onChange={(e) => handlePropertyFieldChange('constructionArea', e.target.value)}
                  variant="outlined"
                  helperText={propertyFieldHelperText('constructionArea')}
                  inputProps={{ maxLength: FIELD_LIMITS.constructionArea.digits, inputMode: 'decimal' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Área de Terreno (m²)"
                  value={editBranch.landArea}
                  onChange={(e) => handlePropertyFieldChange('landArea', e.target.value)}
                  variant="outlined"
                  helperText={propertyFieldHelperText('landArea')}
                  inputProps={{ maxLength: FIELD_LIMITS.landArea.digits, inputMode: 'decimal' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#000000' }}>Número de Niveles</InputLabel>
                  <Select
                    value={editBranch.constructionLevels}
                    label="Número de Niveles"
                    onChange={(e) => setEditBranch({ ...editBranch, constructionLevels: e.target.value })}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                    }}
                  >
                    <MenuItem value="">Seleccionar</MenuItem>
                    {[...Array(10)].map((_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>{i + 1} {i + 1 === 1 ? 'Nivel' : 'Niveles'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#000000' }}>Régimen de Propiedad</InputLabel>
                  <Select
                    value={editBranch.propertyRegime || 'PROPIO'}
                    label="Régimen de Propiedad"
                    onChange={(e) => setEditBranch({ ...editBranch, propertyRegime: e.target.value })}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                    }}
                  >
                    <MenuItem value="PROPIO">Propio</MenuItem>
                    <MenuItem value="ARRENDADO">Arrendado</MenuItem>
                    <MenuItem value="COMODATO">Comodato</MenuItem>
                    <MenuItem value="OTRO">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Sección de Información Operativa */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: 'white' }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              color: '#000000', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <StoreIcon />
              Información Operativa
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Número de Empleados"
                  value={editBranch.employeesCount}
                  onChange={(e) => handlePropertyFieldChange('employeesCount', e.target.value)}
                  variant="outlined"
                  helperText={propertyFieldHelperText('employeesCount')}
                  inputProps={{ maxLength: FIELD_LIMITS.employeesCount.digits, inputMode: 'numeric' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Aforo Máximo"
                  value={editBranch.maximumCapacity}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    if (numericValue.length <= 5) {
                      setEditBranch({ ...editBranch, maximumCapacity: numericValue });
                    }
                  }}
                  variant="outlined"
                  helperText="Máximo 5 dígitos"
                  inputProps={{ maxLength: FIELD_LIMITS.maximumCapacity.digits, inputMode: 'numeric' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Población Flotante"
                  value={editBranch.floatingPopulation}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    if (numericValue.length <= 5) {
                      setEditBranch({ ...editBranch, floatingPopulation: numericValue });
                    }
                  }}
                  variant="outlined"
                  helperText="Máximo 5 dígitos"
                  inputProps={{ maxLength: FIELD_LIMITS.floatingPopulation.digits, inputMode: 'numeric' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Operativo — Inicio"
                  value={editBranch.operatingHoursStart}
                  onChange={(e) => setEditBranch({ ...editBranch, operatingHoursStart: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Operativo — Fin"
                  value={editBranch.operatingHoursEnd}
                  onChange={(e) => setEditBranch({ ...editBranch, operatingHoursEnd: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Administrativo — Inicio"
                  value={editBranch.administrativeHoursStart}
                  onChange={(e) => setEditBranch({ ...editBranch, administrativeHoursStart: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  helperText="Horario de atención administrativa"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  type="time"
                  label="Horario Administrativo — Fin"
                  value={editBranch.administrativeHoursEnd}
                  onChange={(e) => setEditBranch({ ...editBranch, administrativeHoursEnd: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Actividades Realizadas"
                  value={editBranch.mainActivities}
                  inputProps={{ maxLength: FIELD_LIMITS.mainActivities }}
                  onChange={(e) => setEditBranch({ ...editBranch, mainActivities: e.target.value })}
                  variant="outlined"
                  multiline
                  rows={3}
                  helperText="Descripción de las actividades principales realizadas en la sucursal"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#000000' }}>Estado</InputLabel>
                  <Select
                    value={editBranch.isActive}
                    label="Estado"
                    onChange={(e) => setEditBranch({ ...editBranch, isActive: e.target.value })}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                    }}
                  >
                    <MenuItem value={true}>
                      <Chip label="Activa" color="success" size="small" />
                    </MenuItem>
                    <MenuItem value={false}>
                      <Chip label="Inactiva" color="error" size="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Nota sobre documentos */}
          <Paper elevation={2} sx={{ p: 3, m: 3, mb: 2, borderRadius: 2, bgcolor: '#fff3cd' }}>
            <Typography variant="body2" sx={{ 
              color: '#856404',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <DescriptionIcon />
              <strong>Nota:</strong> Los documentos se pueden agregar después de crear la sucursal, editándola.
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCreateCloseAttempt}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleCreateSubmit} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              }
            }}
          >
            Crear Sucursal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para agregar nuevo sector empresarial */}
      <AddBusinessSectorModal
        open={sectorModalOpen}
        onClose={() => setSectorModalOpen(false)}
        onSectorAdded={handleSectorAdded}
      />

      {/* Dialog para Gestión de Documentos */}
      <UploadDocumentosDialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        branch={selectedBranchForDocs}
        onSuccess={handleDocumentUploadSuccess}
        onError={handleDocumentUploadError}
      />

      {/* Dialog de Validación y Generación de Documentos */}
      <Dialog
        open={docValidationDialogOpen}
        onClose={() => setDocValidationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #6a1b9a 0%, #4a148c 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2
        }}>
          <AssignmentIcon />
          Generación de Documentos — {selectedBranchForGen?.branchName}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          {validationLoading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#6a1b9a' }} />
              <Typography sx={{ mt: 2 }}>Validando campos...</Typography>
            </Box>
          ) : validationData ? (
            <Box>
              {/* Sección RIC */}
              {validationData.ric && (
                <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, border: validationData.ric.ready ? '2px solid #4caf50' : '2px solid #ff9800' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getReadinessIcon(validationData.ric)}
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        RIC — Reporte de Inspección
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${validationData.ric.completionPercentage}% completo`}
                      color={validationData.ric.ready ? 'success' : validationData.ric.completionPercentage >= 70 ? 'warning' : 'error'}
                      size="small"
                    />
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={validationData.ric.completionPercentage} 
                    sx={{ height: 8, borderRadius: 4, mb: 2, bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { 
                        bgcolor: validationData.ric.ready ? '#4caf50' : validationData.ric.completionPercentage >= 70 ? '#ff9800' : '#f44336',
                        borderRadius: 4
                      }
                    }}
                  />

                  {/* Campos faltantes requeridos */}
                  {validationData.ric.missingRequiredBySection && Object.keys(validationData.ric.missingRequiredBySection).length > 0 && (
                    <Collapse in={true}>
                      <Box sx={{ bgcolor: '#ffebee', borderRadius: 1, p: 1.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#c62828', mb: 0.5 }}>
                          Campos obligatorios faltantes:
                        </Typography>
                        {Object.entries(validationData.ric.missingRequiredBySection).map(([section, fields]) => 
                          renderFieldSection(section, fields, 'required')
                        )}
                      </Box>
                    </Collapse>
                  )}

                  {/* Campos faltantes opcionales */}
                  {validationData.ric.missingOptionalBySection && Object.keys(validationData.ric.missingOptionalBySection).length > 0 && (
                    <Collapse in={true}>
                      <Box sx={{ bgcolor: '#fff3e0', borderRadius: 1, p: 1.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e65100', mb: 0.5 }}>
                          Campos opcionales faltantes:
                        </Typography>
                        {Object.entries(validationData.ric.missingOptionalBySection).map(([section, fields]) => 
                          renderFieldSection(section, fields, 'optional')
                        )}
                      </Box>
                    </Collapse>
                  )}

                  <Divider sx={{ my: 1.5 }} />
                  <Button
                    variant="contained"
                    startIcon={generatingDoc === 'ric' ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    disabled={generatingDoc !== null}
                    onClick={() => handleGenerateDocument(selectedBranchForGen.id, 'ric')}
                    sx={{ 
                      textTransform: 'none', fontWeight: 'bold',
                      background: validationData.ric.ready 
                        ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)' 
                        : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                      '&:hover': {
                        background: validationData.ric.ready 
                          ? 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)'
                          : 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                      }
                    }}
                  >
                    {generatingDoc === 'ric' ? 'Generando...' : 'Generar RIC'}
                  </Button>
                  {!validationData.ric.ready && (
                    <Typography variant="caption" sx={{ ml: 2, color: '#e65100' }}>
                      Se generará con campos vacíos donde falte información
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Sección PIPC */}
              {validationData.pipc && (
                <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2, border: validationData.pipc.ready ? '2px solid #4caf50' : '2px solid #ff9800' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getReadinessIcon(validationData.pipc)}
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        PIPC — Programa Interno de Protección Civil
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${validationData.pipc.completionPercentage}% completo`}
                      color={validationData.pipc.ready ? 'success' : validationData.pipc.completionPercentage >= 70 ? 'warning' : 'error'}
                      size="small"
                    />
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={validationData.pipc.completionPercentage} 
                    sx={{ height: 8, borderRadius: 4, mb: 2, bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { 
                        bgcolor: validationData.pipc.ready ? '#4caf50' : validationData.pipc.completionPercentage >= 70 ? '#ff9800' : '#f44336',
                        borderRadius: 4
                      }
                    }}
                  />

                  {/* Campos faltantes requeridos */}
                  {validationData.pipc.missingRequiredBySection && Object.keys(validationData.pipc.missingRequiredBySection).length > 0 && (
                    <Collapse in={true}>
                      <Box sx={{ bgcolor: '#ffebee', borderRadius: 1, p: 1.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#c62828', mb: 0.5 }}>
                          Campos obligatorios faltantes:
                        </Typography>
                        {Object.entries(validationData.pipc.missingRequiredBySection).map(([section, fields]) => 
                          renderFieldSection(section, fields, 'required')
                        )}
                      </Box>
                    </Collapse>
                  )}

                  {/* Campos faltantes opcionales */}
                  {validationData.pipc.missingOptionalBySection && Object.keys(validationData.pipc.missingOptionalBySection).length > 0 && (
                    <Collapse in={true}>
                      <Box sx={{ bgcolor: '#fff3e0', borderRadius: 1, p: 1.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e65100', mb: 0.5 }}>
                          Campos opcionales faltantes:
                        </Typography>
                        {Object.entries(validationData.pipc.missingOptionalBySection).map(([section, fields]) => 
                          renderFieldSection(section, fields, 'optional')
                        )}
                      </Box>
                    </Collapse>
                  )}

                  <Divider sx={{ my: 1.5 }} />
                  <Button
                    variant="contained"
                    startIcon={generatingDoc === 'pipc' ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    disabled={generatingDoc !== null}
                    onClick={() => handleGenerateDocument(selectedBranchForGen.id, 'pipc')}
                    sx={{ 
                      textTransform: 'none', fontWeight: 'bold',
                      background: validationData.pipc.ready 
                        ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)' 
                        : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                      '&:hover': {
                        background: validationData.pipc.ready 
                          ? 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)'
                          : 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                      }
                    }}
                  >
                    {generatingDoc === 'pipc' ? 'Generando...' : 'Generar PIPC'}
                  </Button>
                  {!validationData.pipc.ready && (
                    <Typography variant="caption" sx={{ ml: 2, color: '#e65100' }}>
                      Se generará con campos vacíos donde falte información
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Info: no hay cotización vinculada */}
              {!validationData.ric && !validationData.pipc && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  No se encontró cotización vinculada a esta sucursal. Debe vincular una cotización antes de generar documentos.
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No se pudo obtener la validación. Intente de nuevo.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => setDocValidationDialogOpen(false)} 
            variant="outlined"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirmación de descarte de datos */}
      <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
          ¿Descartar cambios?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tienes información ingresada en el formulario. Si cierras ahora, se perderán todos los datos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setDiscardConfirmOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none', borderColor: '#cccccc', color: '#666666' }}
          >
            Seguir editando
          </Button>
          <Button
            onClick={() => {
              setDiscardConfirmOpen(false);
              setCreateDialogOpen(false);
              setCreateAddressErrors({});
              resetForm();
            }}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
          >
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={8000}
        onClose={() => { setSuccess(null); setGeneratedDocFolderPath(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => { setSuccess(null); setGeneratedDocFolderPath(null); }}
          severity="success"
          variant="filled"
          sx={{ alignItems: 'center' }}
          action={
            generatedDocFolderPath && (
              <Button color="inherit" size="small" onClick={handleGoToGestionDocumental} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
                Ver en Gestión Documental
              </Button>
            )
          }
        >
          {success}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default GestionSucursales;
